'use client'

import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import Link from 'next/link'
import { useConfig } from 'nextra-theme-docs'
import { Fragment } from 'react'

// When there are more items than this, collapse the middle ones into a dropdown (desktop)
const COLLAPSE_THRESHOLD = 2

function renderItems(visibleItems, hiddenItems) {
  return visibleItems.map((item, index) => (
    <Fragment key={index}>
      {index > 0 && <BreadcrumbSeparator />}
      <BreadcrumbItem>
        {item === null ? (
          <DropdownMenu>
            <DropdownMenuTrigger aria-label="Show hidden breadcrumb levels" className="flex items-center gap-1">
              <BreadcrumbEllipsis />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {hiddenItems.map((hidden, i) => (
                <DropdownMenuItem key={i} asChild>
                  <Link href={hidden.href || '#'}>{hidden.title}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : item.isLast ? (
          <BreadcrumbPage>{item.title}</BreadcrumbPage>
        ) : (
          <BreadcrumbLink asChild>
            <Link href={item.href || '#'} prefetch={false}>
              {item.title}
            </Link>
          </BreadcrumbLink>
        )}
      </BreadcrumbItem>
    </Fragment>
  ))
}

export function DocsBreadcrumb() {
  const { normalizePagesResult } = useConfig()
  const { activePath, activeType } = normalizePagesResult

  if (activeType === 'page' || !activePath?.length) return null

  const items = activePath.map((item, index, arr) => ({
    title: item.title,
    href: index < arr.length - 1 ? item.route || '' : '',
    isLast: index === arr.length - 1,
  }))

  // Desktop: collapse middle items when more than COLLAPSE_THRESHOLD
  const shouldCollapseDesktop = items.length > COLLAPSE_THRESHOLD
  const desktopHiddenItems = shouldCollapseDesktop ? items.slice(1, -(COLLAPSE_THRESHOLD - 1)) : []
  const desktopVisibleItems = shouldCollapseDesktop
    ? [items[0], null, ...items.slice(-(COLLAPSE_THRESHOLD - 1))]
    : items

  // Mobile: always show only first + ··· + last (··· expands all middle items)
  const shouldCollapseMobile = items.length > 2
  const mobileHiddenItems = shouldCollapseMobile ? items.slice(1, -1) : []
  const mobileVisibleItems = shouldCollapseMobile ? [items[0], null, items[items.length - 1]] : items

  return (
    <Breadcrumb className="docs-breadcrumb mb-0!">
      {/* Mobile: first + ··· + last only */}
      <BreadcrumbList className="md:hidden">{renderItems(mobileVisibleItems, mobileHiddenItems)}</BreadcrumbList>
      {/* Desktop: collapse middle when > COLLAPSE_THRESHOLD */}
      <BreadcrumbList className="hidden md:flex">{renderItems(desktopVisibleItems, desktopHiddenItems)}</BreadcrumbList>
    </Breadcrumb>
  )
}
